import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, HostListener, Inject, Input, OnChanges, Output, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import Compressor from 'compressorjs';
import { WebcamImage } from 'ngx-webcam';
import { PDFDocument } from 'pdf-lib';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { UploadComponentString } from 'src/app/shared/content/content';
import Swal from 'sweetalert2';
import { FilesService } from '../../services/files.service';
import { UserAgentService } from '../../services/user-agent.service';

@Component({
    selector: 'app-multi-uploader',
    templateUrl: './multi-uploader.component.html',
    styleUrls: ['./multi-uploader.component.scss', '../modal/modal.component.scss'],
    standalone: false
})
export class MultiUploaderComponent implements OnChanges {
  // Outputs
  @Output() filesSelected = new EventEmitter<File[]>();
  @Output() allFilesUploaded = new EventEmitter<boolean>();
  @Output() fileUploaded = new EventEmitter<{ file: File, index: number, fileName?: string }>();
  @Output() fileDeleted = new EventEmitter<{ fileName: string, blobFileId?: number | null }>();

  // Inputs - compatibilidad con upload component
  @Input() public documentType: any;
  @Input() public checklist: boolean = false;
  @Input() public responsive: boolean = false;
  @Input() public isPendence: boolean = false;
  @Input() public preview: boolean = false;
  @Input() public userId?: string;
  @Input() public clientType: string = 'PF';
  @Input() public acceptAllFiles: boolean = false;
  @Input() public maxFiles: number = 5;
  @Input() public existingFiles: any[] = [];
	@Input() public readonly: boolean = false;

  // Estado del componente
  public isMobile: boolean = false;
  public isPending = false;
  public fieldId = Math.floor(Math.random() * Date.now());
  public showSnapshotModal = false;
  public error = '';
  public webcamWidth = 600;
  public webcamHeight = 500;

  // Estado drag & drop
  public isDragging = false;
  public errorMessage: string | null = null;

  // Estado de eliminación global
  public isDeleting: boolean = false;

  // Archivos múltiples
  public uploadedFiles: Array<{ name: string, success: boolean, uploading: boolean, previewUrl?: string, file?: File, previewLoading?: boolean, previewProgress?: number | null, deleting?: boolean }> = [];
  public currentFileIndex: number = 0;

  // Webcam
  private trigger: Subject<void> = new Subject<void>();
  private webcamImage!: WebcamImage;
  private capturedImageBase64 = '';

  // Modal de vista previa
  public showPreviewModal = false;
  public previewFileUrl: string | null = null;
  public sanitizedPreviewFileUrl: SafeResourceUrl | null = null;
  public previewFileName: string = '';
  public previewFileType: string = '';

  // Textos
  uploadComponentString = UploadComponentString;

  // Configuración de validación
  private readonly allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  private readonly allowedExtensions = ['.pdf', '.jpeg', '.jpg', '.png'];
  private readonly maxSizeInBytes = 60 * 1024 * 1024; // 60MB

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private filesService: FilesService,
    private userAgent: UserAgentService,
    private ref: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {
    this.isMobile = userAgent.verifyMobile();
  }

  ngOnInit(): void {
    this.convertUploadLabelName();
    this.loadExistingFiles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar cambios en existingFiles
    if (changes['existingFiles'] && !changes['existingFiles'].firstChange) {
      console.log('🔄 multi-uploader ngOnChanges - existingFiles cambió', {
        previousValue: changes['existingFiles'].previousValue,
        currentValue: changes['existingFiles'].currentValue,
        currentLength: changes['existingFiles'].currentValue?.length
      });

      // Limpiar archivos existentes precargados (mantener solo los nuevos subidos)
      const newUploadedFiles = this.uploadedFiles.filter(file => !file.success || file.file);
      this.uploadedFiles = [...newUploadedFiles];

      // Cargar los nuevos archivos existentes
      this.loadExistingFiles();
    }
  }

  private loadExistingFiles(): void {
    if (!this.existingFiles || this.existingFiles.length === 0) {
      console.log('⚠️ multi-uploader - No hay existingFiles para cargar');
      return;
    }

    // Obtener los nombres de archivos ya cargados para evitar duplicados
    const existingFileNames = this.uploadedFiles
      .filter(f => f.success && !f.file)
      .map(f => f.name);

    console.log('📦 multi-uploader - Cargando archivos existentes', {
      existingFilesCount: this.existingFiles.length,
      alreadyLoadedCount: existingFileNames.length,
      alreadyLoaded: existingFileNames
    });

    let addedCount = 0;
    this.existingFiles.forEach((blob: any) => {
      // Extraer solo el nombre del archivo después de 'ondemand-v3/'
      const fileName = this.extractFileNameFromUrl(blob.url);

      // Verificar si ya está cargado para evitar duplicados
      if (existingFileNames.includes(fileName)) {
        console.log(`⏭️ multi-uploader - Archivo ya cargado, omitiendo: ${fileName}`);
        return;
      }

      this.uploadedFiles.push({
        name: fileName,
        success: true,
        uploading: false,
        previewUrl: undefined, // Sin preview porque el contenedor es privado
        file: undefined, // Los archivos ya subidos no tienen el objeto File local
        previewLoading: false,
        previewProgress: null,
        deleting: false
      });
      addedCount++;
    });

    if (addedCount > 0) {
      console.log(`✅ multi-uploader - Se agregaron ${addedCount} archivos nuevos`);
      // Forzar detección de cambios
      this.uploadedFiles = [...this.uploadedFiles];
      this.detectChanges();
    } else {
      console.log('⚠️ multi-uploader - No se agregaron archivos nuevos (todos ya estaban cargados)');
    }
  }

  private extractFileNameFromUrl(url: string): string {
    try {
      // Buscar la posición después de 'ondemand-v3/'
      const containerPath = 'ondemand-v3/';
      const containerIndex = url.indexOf(containerPath);

      if (containerIndex !== -1) {
        // Extraer todo después de 'ondemand-v3/'
        const fileName = url.substring(containerIndex + containerPath.length);
        return fileName || 'archivo-existente';
      }

      // Fallback: extraer solo el último segmento de la URL
      const urlParts = url.split('/');
      return urlParts[urlParts.length - 1] || 'archivo-existente';
    } catch (error) {
      return 'archivo-existente';
    }
  }

  private convertUploadLabelName(): void {
    if (this.documentType?.label === 'CNH') this.documentType.label = 'Enviar foto da CNH';
    if (this.documentType?.label === 'Selfie') this.documentType.label = 'Enviar selfie';
    if (this.documentType?.label === 'Requerimento empresário') this.documentType.label = 'Contrato Social+ Última  alteração contratual';
  }

  onBrowseClick(input: HTMLInputElement) {
		if (this.readonly) return;
    input.click();
  }

  onFileChange(event: Event) {
		if (this.readonly) return;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleMultipleFiles(input.files);
    }
    // Reset input to allow reselecting same file
    if (input) input.value = '';
  }

  onDragOver(event: DragEvent) {
		if (this.readonly) { event.preventDefault(); return; }
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
		if (this.readonly) { event.preventDefault(); return; }
    event.preventDefault();
    this.isDragging = false;
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.handleMultipleFiles(event.dataTransfer.files);
    }
  }

  clear() {
    // Limpiar todas las URLs de vista previa
    this.uploadedFiles.forEach(file => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });
    this.uploadedFiles = [];
    this.currentFileIndex = 0;
    this.errorMessage = null;
    this.filesSelected.emit([]);
  }

  removeFile(index: number) {
		 if (this.readonly) return;
    // Prevenir eliminaciones simultáneas
    if (this.isDeleting) {
      return;
    }

    const file = this.uploadedFiles[index];
    if (!file) {
      return;
    }

    const fileName = file.name;

    // Mostrar modal de confirmación antes de eliminar
    Swal.fire({
      title: '¿Eliminar archivo?',
      text: `¿Estás seguro de que deseas eliminar el archivo "${fileName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-primary swal-confirm-button',
        cancelButton: 'btn btn-secondary swal-cancel-button'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.executeFileDeletion(index, fileName);
      }
    });
  }

  private executeFileDeletion(index: number, fileName: string): void {
    // Prevenir eliminaciones simultáneas
    if (this.isDeleting) {
      return;
    }

    const file = this.uploadedFiles[index];
    if (!file) {
      return;
    }

    // Activar estado de eliminación global y del archivo específico
    this.isDeleting = true;
    file.deleting = true;
    this.uploadedFiles = [...this.uploadedFiles];
    this.detectChanges();

    // Si el archivo tiene previewUrl, revocarlo para liberar memoria
    if (file.previewUrl) {
      URL.revokeObjectURL(file.previewUrl);
    }

    // Si el archivo tiene nombre (archivo precargado o subido), eliminarlo del backend
    if (fileName && file.success) {
      // Buscar el id en existingFiles basado en el nombre del archivo extraído de la URL
      let blobFileId: number | null = null;
      if (this.existingFiles && this.existingFiles.length > 0) {
        const blobFile = this.existingFiles.find((blob: any) => {
          if (!blob.url) return false;
          const blobFileName = this.extractFileNameFromUrl(blob.url);
          return blobFileName === fileName;
        });

        if (blobFile && blobFile.id) {
          blobFileId = blobFile.id;
        }
      }

      // Eliminar de forma transaccional (blob storage y base de datos)
      this.filesService.deleteBlobFileTransactional(fileName, blobFileId).subscribe({
        next: (success) => {
          if (success) {
            console.log('Archivo eliminado correctamente (blob storage y base de datos)');
            // Eliminar del array solo si ambas operaciones fueron exitosas
            this.uploadedFiles.splice(index, 1);
            this.uploadedFiles = [...this.uploadedFiles];
            this.detectChanges();

            // Emitir evento al componente padre para que actualice su array
            this.fileDeleted.emit({ fileName, blobFileId });

            // Mantener isDeleting en true mientras el toast está visible
            // (ya está en true desde el inicio de la eliminación)

            // Mostrar mensaje toast de éxito
            const toast = Swal.fire({
              title: 'Archivo eliminado',
              text: `El archivo "${fileName}" ha sido eliminado correctamente.`,
              icon: 'success',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 2000,
              timerProgressBar: true,
              didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
              }
            });

            // Forzar detección de cambios para asegurar que los botones se deshabiliten visualmente
            this.detectChanges();

            // Esperar a que el toast se cierre antes de permitir nuevas eliminaciones
            toast.then(() => {
              this.isDeleting = false;
              this.detectChanges();
            });
          } else {
            // Si falló pero no hay error, resetear estado
            this.isDeleting = false;
            file.deleting = false;
            this.uploadedFiles = [...this.uploadedFiles];
            this.detectChanges();
          }
        },
        error: (err) => {
          this.isDeleting = false;
          file.deleting = false;
          console.error('Error al eliminar archivo:', err);
          if (err.dbDeleted) {
            // El registro de BD fue eliminado pero falló la eliminación del blob
            this.errorMessage = 'El registro fue eliminado de la base de datos, pero hubo un error al eliminar el archivo del almacenamiento.';
            // Aún así eliminamos del array visualmente ya que el registro de BD fue eliminado
            this.uploadedFiles.splice(index, 1);
            this.uploadedFiles = [...this.uploadedFiles];

            // Emitir evento al componente padre para que actualice su array
            this.fileDeleted.emit({ fileName, blobFileId });
          } else {
            // Falló la eliminación de BD, no eliminamos del array
            this.errorMessage = 'No fue posible eliminar el archivo.';
            this.uploadedFiles = [...this.uploadedFiles];
          }
          setTimeout(() => this.errorMessage = null, 5000);
          this.detectChanges();
        }
      });
    } else {
      // Si no tiene nombre o no está subido exitosamente, solo eliminar del array local
      file.deleting = false;
      this.uploadedFiles.splice(index, 1);
      this.uploadedFiles = [...this.uploadedFiles];

      // Mantener isDeleting en true mientras el toast está visible
      // (ya está en true desde el inicio de la eliminación)

      // Mostrar mensaje toast de éxito
      const toast = Swal.fire({
        title: 'Archivo eliminado',
        text: `El archivo "${fileName}" ha sido eliminado correctamente.`,
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
      });

      // Forzar detección de cambios para asegurar que los botones se deshabiliten visualmente
      this.detectChanges();

      // Esperar a que el toast se cierre antes de permitir nuevas eliminaciones
      toast.then(() => {
        this.isDeleting = false;
        this.detectChanges();
      });
    }
  }

  private generatePreview(file: File): string | undefined {
    if (file.type.startsWith('image/') || file.type === 'application/pdf') {
      return URL.createObjectURL(file);
    }
    return undefined;
  }

  get canAddMore(): boolean {
    return this.uploadedFiles.length < this.maxFiles;
  }

  get uploadedSuccessfully(): number {
    return this.uploadedFiles.filter(f => f.success).length;
  }

  private handleMultipleFiles(fileList: FileList) {
    const filesArray = Array.from(fileList);
    const availableSlots = this.maxFiles - this.uploadedFiles.length;

    if (filesArray.length > availableSlots) {
      this.errorMessage = `No puedes subir más archivos. Máximo ${this.maxFiles} archivos en total.`;
      setTimeout(() => this.errorMessage = null, 5000);
      return;
    }

    // Procesar cada archivo
    filesArray.forEach((file, index) => {
      const previewUrl = this.generatePreview(file);
      this.uploadedFiles.push({
        name: file.name,
        success: false,
        uploading: true,
        previewUrl,
        file,
        previewLoading: false,
        previewProgress: null,
        deleting: false
      });
      const currentIndex = this.uploadedFiles.length - 1;
      this.compressDocument(this.documentType?.type, file, currentIndex);
    });
  }

  // Métodos de compresión
  public compressDocument(documentType: any, file: any, fileIndex: number): void {
    this.isPending = true;
    this.errorMessage = null;

    if (file.files) {
      file = file.files[0];
    }

    // Validación de formato para PJ
    if (this.clientType === 'PJ' && !file.type.includes('pdf') && !file.type.includes('jpg') && !file.type.includes('jpeg') && !file.type.includes('png') && !this.acceptAllFiles) {
      Swal.fire({
        title: '¡Atención!',
        text: `Sólo se aceptan archivos en formato PDF o Imagens (.JPG, .JPEG, .PNG).`,
        icon: 'warning',
        confirmButtonText: 'Ok',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary',
        },
      });
      this.uploadedFiles[fileIndex].uploading = false;
      this.uploadedFiles[fileIndex].success = false;
      this.isPending = false;
      return;
    }

    // Validación de tamaño
    if (file.size > this.maxSizeInBytes) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      this.errorMessage = `El archivo "${file.name}" es muy grande (${sizeMB}MB). El tamaño máximo permitido es 60MB.`;
      this.uploadedFiles[fileIndex].uploading = false;
      this.uploadedFiles[fileIndex].success = false;
      this.isPending = false;
      setTimeout(() => this.errorMessage = null, 5000);
      return;
    }

    if (file.type.includes('pdf')) {
      this.compressIfPdf(documentType, file, fileIndex);
    } else {
      this.compressIfImage(documentType, file, fileIndex);
    }
  }

  private compressIfImage(documentType: any, file: any, fileIndex: number): void {
    const rawCompressedFile: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    const compressingError: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    rawCompressedFile.subscribe({
      next: rawCompressedFile => {
        if (rawCompressedFile) {
          const compressedFile = new File([rawCompressedFile], rawCompressedFile.name, {
            type: rawCompressedFile.type,
          });
          this.uploadDocument(documentType, compressedFile, fileIndex);
        }
      },
      error: (err: any) => {
        // Manejo de error: mostrar mensaje controlado y detener la subida
        console.error('Error al comprimir imagen:', err);
        this.uploadedFiles[fileIndex].success = false;
        this.uploadedFiles[fileIndex].uploading = false;
        this.isPending = false;
        this.error = err;
        Swal.fire({
          title: 'Error al procesar la imagen',
          text: `El archivo "${file.name}" no se pudo comprimir. Verifica que sea una imagen válida y vuelve a intentarlo.`,
          icon: 'error',
          confirmButtonText: 'Entendido',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-primary',
          },
        });

        this.detectChanges();
      },
    });

    compressingError.subscribe({
      next: (errorMessage: any) => {
        if (errorMessage) {
          // Manejo de error: mostrar mensaje controlado y detener la subida
          console.error('Error en compresión de imagen:', errorMessage);
          this.uploadedFiles[fileIndex].success = false;
          this.uploadedFiles[fileIndex].uploading = false;
          this.isPending = false;
          this.error = errorMessage;

          Swal.fire({
            title: 'Error al comprimir la imagen',
            text: `El archivo "${file.name}" no se pudo comprimir. Verifica que sea una imagen válida y vuelve a intentarlo.`,
            icon: 'error',
            confirmButtonText: 'Entendido',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary',
            },
          });

          this.detectChanges();
        }
      },
    });

    new Compressor(file, {
      quality: 0.6,
      convertTypes: ['image/png', 'image/webp', 'image/tiff'],
      convertSize: 0,
      success(result) {
        rawCompressedFile.next(result);
      },
      error(err) {
        compressingError.next(err.message);
      },
    });
  }

 private compressIfPdf(documentType: any, pdfFile: any, fileIndex: number): void {
    pdfFile.arrayBuffer().then(async (result: any) => {
        const filePdfBytes = result;

        const header = new Uint8Array(filePdfBytes.slice(0, 5));
        const isPdf = String.fromCharCode(...header) === '%PDF-';

        if (!isPdf) {
            this.uploadedFiles[fileIndex].uploading = false;
            this.uploadedFiles[fileIndex].success = false;
            this.isPending = false;
            Swal.fire({
                title: 'Error en el archivo PDF',
                text: `El archivo "${pdfFile.name}" no es un PDF válido.`,
                icon: 'error',
                confirmButtonText: 'Entendido',
                buttonsStyling: false,
                customClass: { confirmButton: 'btn btn-primary' },
            });
            this.detectChanges();
            return;
        }

        const formattedFilePdf = new File([filePdfBytes], pdfFile.name, { type: pdfFile.type });
        this.uploadDocument(documentType, formattedFilePdf, fileIndex);
    });
}

  private uploadDocument(documentType: any, compressedFile: any, fileIndex: number): void {
    if (this.checklist) {
      this.fileUploaded.emit({ file: compressedFile, index: fileIndex });
      setTimeout(() => {
        this.uploadedFiles[fileIndex].success = true;
        this.uploadedFiles[fileIndex].uploading = false;
        this.checkAllUploaded();
        this.closeModal();
      }, 3000);
      return;
    } else {
      this.filesService.uploadMultipleDocuments(documentType, compressedFile, this.documentType?.documentId, this.userId).subscribe({
        next: (response) => {
          // Actualizar el archivo en el array
          this.uploadedFiles[fileIndex] = {
            ...this.uploadedFiles[fileIndex],
            success: true,
            uploading: false,
            name: response.data.fileName
          };
          // Forzar actualización del array para que Angular detecte el cambio
          this.uploadedFiles = [...this.uploadedFiles];
          this.closeModal();
          this.fileUploaded.emit({ file: compressedFile, index: fileIndex, fileName: response.data.fileName });
          this.checkAllUploaded();
          this.detectChanges();
        },
        error: (err: any) => {
          // Manejo de error: mostrar mensaje controlado y detener la subida
          console.error('Error al subir archivo:', err);
          this.uploadedFiles[fileIndex].success = false;
          this.uploadedFiles[fileIndex].uploading = false;
          this.isPending = false;
          this.error = err;

          Swal.fire({
            title: 'Error al subir el archivo',
            text: `El archivo "${compressedFile.name}" no se pudo subir. Verifica tu conexión o intenta nuevamente.`,
            icon: 'error',
            confirmButtonText: 'Entendido',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary',
            },
          });

          this.detectChanges();
        },
      });
    }
  }

  private checkAllUploaded(): void {
    const allUploaded = this.uploadedFiles.every(f => !f.uploading);
    const allSuccess = this.uploadedFiles.every(f => f.success);

    if (allUploaded) {
      this.isPending = false;
      if (allSuccess) {
        this.allFilesUploaded.emit(true);
      }
    }
  }

  private detectChanges(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.ref.detectChanges();
    }
  }

  // Métodos de webcam
  private handleWebcamSnapshotSize(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.webcamWidth = 600;
      this.webcamHeight = 500;

      if (window.innerWidth <= 650) {
        this.webcamWidth = 500;
      }

      if (window.innerWidth <= 550) {
        this.webcamWidth = 400;
      }

      if (window.innerWidth <= 450) {
        this.webcamWidth = 300;
        this.webcamHeight = 400;
      }

      if (window.innerWidth <= 350) {
        this.webcamWidth = 250;
      }
    }
  }

  private dataURItoBlob(dataURI: any): Blob | undefined {
    if (isPlatformBrowser(this.platformId)) {
      const byteString = window.atob(dataURI);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const int8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([int8Array], { type: 'image/jpeg' });
      return blob;
    }
    return;
  }

  public triggerSnapshot(): void {
    this.trigger.next(void 0);
  }

  public handleImage(documentType: any, webcamImage: WebcamImage): void {
    if (!this.canAddMore) {
      this.errorMessage = `Ya alcanzaste el límite de ${this.maxFiles} archivos.`;
      setTimeout(() => this.errorMessage = null, 5000);
      return;
    }

    this.webcamImage = webcamImage;
    this.capturedImageBase64 = webcamImage!.imageAsBase64;

    const formattedImageBlob = this.dataURItoBlob(this.capturedImageBase64);
    if (!formattedImageBlob) {
      this.errorMessage = 'Error al procesar la imagen de la cámara.';
      setTimeout(() => this.errorMessage = null, 5000);
      return;
    }

    const timestamp = Date.now();
    const formattedImageFile = new File([formattedImageBlob], `photo-${timestamp}.jpg`, { type: formattedImageBlob.type });
    const previewUrl = this.generatePreview(formattedImageFile);

    this.uploadedFiles.push({
      name: formattedImageFile.name,
      success: false,
      uploading: true,
      previewUrl,
      file: formattedImageFile,
      previewLoading: false,
      previewProgress: null,
      deleting: false
    });
    const currentIndex = this.uploadedFiles.length - 1;
    this.compressDocument(documentType, formattedImageFile, currentIndex);
  }

  public get triggerObservable(): Observable<any> {
    return this.trigger.asObservable();
  }

  public closeModal(): void {
    this.showSnapshotModal = false;
  }

  public openPreviewModal(file: any): void {
    if (file.previewUrl) {
      this.previewFileUrl = file.previewUrl;
      this.previewFileName = file.name;
      // Para archivos nuevos, usar file.file.type, para existentes, detectar por extensión
      this.previewFileType = file.file?.type || this.getFileTypeFromName(file.name);
      // Sanitizar la URL para PDFs
      if (this.previewFileType === 'application/pdf' || (file.name && file.name.toLowerCase().endsWith('.pdf'))) {
        if (this.previewFileUrl) {
          this.sanitizedPreviewFileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewFileUrl);
        }
      } else {
        this.sanitizedPreviewFileUrl = null;
      }
      this.showPreviewModal = true;
      this.detectChanges();
      return;
    }

    // Si no hay previewUrl (archivo precargado), intentar obtenerlo del backend
    if (!file.previewUrl && file.name) {
      this.fetchPreviewFromBackend(file);
      return;
    }

    console.log('No preview URL available and cannot fetch (missing fileName)');
  }

  private fetchPreviewFromBackend(file: any): void {
    try {
      const fileIndex = this.uploadedFiles.findIndex(f => f === file);
      if (fileIndex === -1) {
        console.log('File not found in uploadedFiles array');
        return;
      }

      const fileName = file.name;
      if (!fileName) {
        console.log('Cannot fetch preview: missing fileName');
        return;
      }

      // Actualizar estado de carga para este archivo específico
      this.uploadedFiles[fileIndex].previewLoading = true;
      this.uploadedFiles[fileIndex].previewProgress = null;
      this.uploadedFiles = [...this.uploadedFiles];
      this.detectChanges();

      this.filesService.getBlobFileWithProgress(fileName).subscribe({
        next: (event: any) => {
          // HttpEventType: Sent=0, UploadProgress=1, ResponseHeader=2, DownloadProgress=3, Response=4, User=5
          if (event?.type === 3) {
            if (event.total) {
              this.uploadedFiles[fileIndex].previewProgress = Math.round((event.loaded / event.total) * 100);
            } else {
              this.uploadedFiles[fileIndex].previewProgress = null; // indeterminate
            }
            this.uploadedFiles = [...this.uploadedFiles];
            this.detectChanges();
          } else if (event?.type === 4) {
            const resp = event;
            // Detectar tipo desde Content-Type o desde el nombre del archivo
            let contentType = resp.body?.type || resp.headers.get('Content-Type') || '';
            if (!contentType) {
              contentType = this.getFileTypeFromName(fileName);
            }
            const blob = new Blob([resp.body as any], { type: contentType });

            // Crear previewUrl y actualizar el archivo
            const previewUrl = URL.createObjectURL(blob);
            this.uploadedFiles[fileIndex].previewUrl = previewUrl;
            this.uploadedFiles[fileIndex].previewLoading = false;
            this.uploadedFiles[fileIndex].previewProgress = 100;
            this.uploadedFiles = [...this.uploadedFiles];

            // Abrir modal con la vista previa
            this.previewFileUrl = previewUrl;
            this.previewFileName = fileName;
            this.previewFileType = contentType || this.getFileTypeFromName(fileName);
            // Sanitizar la URL para PDFs
            if (this.previewFileType === 'application/pdf' || (fileName && fileName.toLowerCase().endsWith('.pdf'))) {
              if (previewUrl) {
                this.sanitizedPreviewFileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl);
              }
            } else {
              this.sanitizedPreviewFileUrl = null;
            }
            this.showPreviewModal = true;
            this.detectChanges();

            console.log('Modal opened with fetched blob', {
              fileName,
              contentType,
              previewUrl
            });
          }
        },
        error: (err: any) => {
          console.error('Error fetching preview from backend', err);
          this.uploadedFiles[fileIndex].previewLoading = false;
          this.uploadedFiles[fileIndex].previewProgress = null;
          this.uploadedFiles = [...this.uploadedFiles];
          this.errorMessage = 'No fue posible cargar la vista previa.';
          setTimeout(() => this.errorMessage = null, 5000);
          this.detectChanges();
        }
      });
    } catch (e) {
      console.error('Error in fetchPreviewFromBackend', e);
    }
  }

  private getFileTypeFromName(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      default:
        return '';
    }
  }

  public closePreviewModal(): void {
    this.showPreviewModal = false;
    this.detectChanges();
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.isMobile = this.userAgent.verifyMobile();
    this.handleWebcamSnapshotSize();
  }
}

